// Third-party imports
import React, { useState } from 'react';
import {
  useRecordContext,
  FormDataConsumer,
  useInput,
  SelectInput,
} from 'react-admin';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  InputAdornment,
} from '@mui/material';
import { CopyIcon, ImageWithZoom, Button } from '@troverecommerce/saas-components-ts/build';

// Local imports - types
import type { RepairDetail, RepairItemType, RepairItemIssueType } from '@src/types';

// Local imports - components
import { DashboardCurrency } from '@src/utils/valueFormatters';
import { REJECTION_REASONS } from '@src/types/constants';

interface IssueControlsProps {
  issue: RepairItemIssueType;
  itemIndex: number;
  issueIndex: number;
  currencyCode: string;
  isRepairRejected: boolean;
  isFreeRepair: boolean;
}

const IssueControls: React.FC<IssueControlsProps> = ({
  issue,
  itemIndex,
  issueIndex,
  currencyCode,
  isRepairRejected,
  isFreeRepair,
}) => {
  const issueStateField = useInput({ source: `issueStates.${issue.uuid}`, defaultValue: 'APPROVED' });
  const issuePriceField = useInput({ source: `issuePrices.${issue.uuid}`, defaultValue: issue.price || 0 });

  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [editedPrice, setEditedPrice] = useState('');

  const isIssueRejected = issueStateField.field.value === 'REJECTED';

  const handleEditPrice = (currentPrice: number) => {
    setEditedPrice(String(currentPrice || 0));
    setIsEditingPrice(true);
  };

  const handleSavePrice = () => {
    const newPrice = parseFloat(editedPrice);
    if (!isNaN(newPrice) && newPrice >= 0) {
      issuePriceField.field.onChange(newPrice);
      setIsEditingPrice(false);
    }
  };

  const handleCancelPriceEdit = () => {
    setIsEditingPrice(false);
    setEditedPrice('');
  };

  return (
    <Paper sx={{ marginBottom: 2, padding: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 1.5, marginBottom: 2 }}>
        <Typography variant="body2" fontWeight="bold">Repair Requested:</Typography>
        <Typography variant="body2">{issue.problem || 'N/A'}</Typography>
        <Typography variant="body2" fontWeight="bold">Current Status:</Typography>
        <Typography variant="body2">{issue.status || 'N/A'}</Typography>
        <Typography variant="body2" fontWeight="bold">Repair Fee:</Typography>
        <Typography variant="body2">{DashboardCurrency.displayValue(issue.price, currencyCode)}</Typography>
        {issue.notes && (
          <>
            <Typography variant="body2" fontWeight="bold">Customer Notes:</Typography>
            <Typography variant="body2">{issue.notes}</Typography>
          </>
        )}
      </Box>

      <Divider sx={{ marginY: 2 }} />

      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        Issue Status
      </Typography>

      <RadioGroup
        {...issueStateField.field}
      >
        <FormControlLabel
          value="APPROVED"
          control={<Radio />}
          label="Accept Issue Repair Request"
          disabled={isRepairRejected}
        />
        <FormControlLabel
          value="REJECTED"
          control={<Radio />}
          label="Reject Issue"
          disabled={isRepairRejected}
        />
      </RadioGroup>

      {!isIssueRejected && !isFreeRepair && !isRepairRejected && (
        <Box sx={{ marginTop: 2 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Issue Price:</strong>
          </Typography>
          {!isEditingPrice ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1">
                {DashboardCurrency.displayValue((issuePriceField.field.value ?? issue.price ?? 0), currencyCode)}
              </Typography>
              <Button
                label="Edit"
                variant="text"
                onClick={() => handleEditPrice(issuePriceField.field.value ?? issue.price ?? 0)}
                sx={{
                  textDecoration: 'underline',
                  minWidth: 'auto',
                  padding: 0,
                  textTransform: 'none',
                }}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                label="Issue Price"
                type="number"
                value={editedPrice}
                onChange={e => setEditedPrice(e.target.value)}
                size="small"
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ maxWidth: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {currencyCode}
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                label="OK"
                onClick={handleSavePrice}
              />
              <Button
                label="Cancel"
                onClick={handleCancelPriceEdit}
              />
            </Box>
          )}
        </Box>
      )}

      {isIssueRejected && (
        <Box sx={{ marginTop: 2 }}>
          <SelectInput
            source={`issueRejectionReasons.${issue.uuid}`}
            label="Rejection Reason"
            choices={REJECTION_REASONS.map(reason => ({ id: reason, name: reason }))}
            sx={{ maxWidth: 500 }}
            validate={[required => 'Rejection reason is required']}
          />
        </Box>
      )}

      {isFreeRepair && (
        <Box sx={{ marginTop: 2 }}>
          <Typography variant="caption" color="text.secondary">
            This is a free repair. Price is set to $0.00
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const EditItemsSection = () => {
  const record = useRecordContext<RepairDetail>();

  if (!record || !record.repairItems) return null;

  return (
    <Paper sx={{ padding: 2 }}>
      <Typography variant="h6" gutterBottom>
        Repair Items & Issues
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ marginBottom: 3 }}>
        Review each repair item and its associated issues. You can accept or reject individual issues.
      </Typography>

      <FormDataConsumer>
        {({ formData }) => {
          const isRepairRejected = formData.repairStatus === 'REJECTED';
          const isFreeRepair = formData.repairStatus === 'APPROVED_FREE';

          return (
            <>
              {record.repairItems.map((item: RepairItemType, itemIndex: number) => (
                <Box key={item.uuid || item.uuidPrefix}>
                  <Box sx={{ marginBottom: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: 1 }}>
                      <Typography variant="h6">
                        Item:
                        {' '}
                        {item.uuidPrefix}
                      </Typography>
                      <CopyIcon name="Repair ID" value={item.uuidPrefix} />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 1.5, marginTop: 1 }}>
                      <Typography variant="body2" fontWeight="bold">Item:</Typography>
                      <Typography variant="body2">{item.title || 'N/A'}</Typography>
                      {item.problemTypes && item.problemTypes.length > 0 && (
                        <>
                          <Typography variant="body2" fontWeight="bold">Repairs Requested:</Typography>
                          <Box>
                            {item.problemTypes.map((type, idx) => (
                              <Chip key={idx} label={type} size="small" sx={{ marginRight: 1, marginBottom: 0.5 }} />
                            ))}
                          </Box>
                        </>
                      )}

                      {item.notes && (
                        <>
                          <Typography variant="body2" fontWeight="bold">Customer Notes:</Typography>
                          <Typography variant="body2">{item.notes}</Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  {
                    item.imageUrls && (
                      <Box sx={{ marginBottom: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Customer Photos:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          {Object.values(item.imageUrls as Record<string, string>).map(url => (
                            <Paper key={url}>
                              <ImageWithZoom
                                imageUrl={url}
                                size={{ image: '150px' }}
                                maxZoomWidth="lg"
                              />
                            </Paper>
                          ))}
                        </Box>
                      </Box>
                    )
                  }

                  {
                    item.repairItemIssues && item.repairItemIssues.length > 0 && (
                      <Box>
                        <Divider sx={{ marginY: 2 }} />
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Issues for This Item:
                        </Typography>
                        {isRepairRejected && (
                          <Typography variant="body2" color="error" sx={{ marginBottom: 2 }}>
                            All issues are automatically rejected when the entire repair is rejected.
                          </Typography>
                        )}
                        {item.repairItemIssues.map((issue: RepairItemIssueType, issueIdx: number) => (
                          <IssueControls
                            key={issue.uuid || issueIdx}
                            issue={issue}
                            itemIndex={itemIndex}
                            issueIndex={issueIdx}
                            currencyCode={record.currencyCode || 'USD'}
                            isRepairRejected={isRepairRejected}
                            isFreeRepair={isFreeRepair}
                          />
                        ))}
                      </Box>
                    )
                  }
                </Box>
              ))}
            </>
          );
        }}
      </FormDataConsumer>
    </Paper>
  );
};

export default EditItemsSection;
